/**
 * Approval rules: for each kind of document and amount band, the route.
 *
 * An Admin drafts a rule and submits it; a Finance Head approves it from the
 * approval queue; only then does it route anything. The rule it replaces ends
 * the day before the new one starts, and nothing already submitted changes.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, DOC_KIND_LABELS, formatInr, type Department, type Policy } from '../api'
import { useAuth } from '../auth'
import { ErrorNote, Field, Loading, Note, Stat } from '../components/ui'
import { Pager, usePaged } from '../components/Pager'
import { SidePanel, Tabs, matches, rowProps } from '../components/SidePanel'

type StepKind = 'department_authority' | 'permission' | 'named_user'

interface StepDraft {
  kind: StepKind
  label: string
  permission: string
  named_user_id: string
  required_count: number
}

interface RouteOptions {
  doc_kinds: string[]
  permissions: string[]
  people: { id: string; name: string; role: string }[]
}

const STATUS_CLASS: Record<string, string> = {
  active: 'pass',
  draft: 'closed',
  pending_approval: 'advisory',
  rejected: 'blocking',
  superseded: 'closed',
}

function today(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function rupees(paise: number | null): string {
  return paise === null ? '' : String(paise / 100)
}

function toPaise(value: string): number | null {
  if (value.trim() === '') return null
  return Math.round(Number(value) * 100)
}

function band(p: Policy): string {
  const to = p.max_amount_paise === null ? 'and above' : `to ${formatInr(p.max_amount_paise)}`
  return `${formatInr(p.min_amount_paise)} ${to}`
}

const DEFAULT_STEPS: StepDraft[] = [
  {
    kind: 'department_authority',
    label: 'Department approval',
    permission: '',
    named_user_id: '',
    required_count: 1,
  },
  {
    kind: 'permission',
    label: 'Finance Head',
    permission: '',
    named_user_id: '',
    required_count: 1,
  },
]

type Tab = 'active' | 'pending' | 'ended' | 'all'

const STATUS_LABEL: Record<string, string> = {
  active: 'in force',
  draft: 'draft',
  pending_approval: 'with the Finance Head',
  rejected: 'rejected',
  superseded: 'replaced',
}

function Route({ policy }: { policy: Policy }) {
  return (
    <span className="route-inline">
      {policy.steps.map((s, i) => (
        <span key={s.step_no}>
          {i > 0 && <span className="arrow">→</span>}
          <span className="step">
            {s.label}
            {s.named_user_name && <span className="muted"> ({s.named_user_name})</span>}
            {s.required_count > 1 && <span className="muted"> ×{s.required_count}</span>}
          </span>
        </span>
      ))}
    </span>
  )
}

export default function ApprovalPolicies() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const configurable = can('org.approval_rule.configure')

  const policies = useQuery({
    queryKey: ['approvals', 'policies'],
    queryFn: () => api.get<Policy[]>('/approvals/policies'),
  })
  const options = useQuery({
    queryKey: ['approvals', 'route-options'],
    queryFn: () => api.get<RouteOptions>('/approvals/route-options'),
  })
  const departments = useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get<Department[]>('/org/departments'),
    enabled: can('org.department.view'),
  })

  const [editing, setEditing] = useState<Policy | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [open, setOpen] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>('active')
  const [docKind, setDocKind] = useState('')
  const [search, setSearch] = useState('')

  const submit = useMutation({
    mutationFn: (id: string) => api.post<Policy>(`/approvals/policies/${id}/submit`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['approvals'] }),
  })

  const rows = [...(policies.data ?? [])].sort(
    (a, b) =>
      (DOC_KIND_LABELS[a.doc_kind] ?? a.doc_kind).localeCompare(
        DOC_KIND_LABELS[b.doc_kind] ?? b.doc_kind,
      ) || a.min_amount_paise - b.min_amount_paise,
  )
  const inTab = (p: Policy) =>
    tab === 'all' ||
    (tab === 'active' && p.status === 'active') ||
    (tab === 'pending' && ['draft', 'pending_approval', 'rejected'].includes(p.status)) ||
    (tab === 'ended' && p.status === 'superseded')
  const shown = rows.filter(
    (p) =>
      inTab(p) &&
      (!docKind || p.doc_kind === docKind) &&
      matches(
        search,
        DOC_KIND_LABELS[p.doc_kind] ?? p.doc_kind,
        p.department_name,
        p.note,
        ...p.steps.map((s) => s.label),
        ...p.steps.map((s) => s.named_user_name),
      ),
  )
  const paged = usePaged(shown, `${tab}|${docKind}|${search}`)

  if (policies.isLoading) return <Loading what="Loading approval rules" />
  const active = rows.filter((p) => p.status === 'active')
  const pending = rows.filter((p) => ['draft', 'pending_approval', 'rejected'].includes(p.status))
  const provisional = active.filter((p) => p.note)
  const covered = new Set(active.map((p) => p.doc_kind))
  const kinds = Array.from(new Set(rows.map((p) => p.doc_kind))).sort((a, b) =>
    (DOC_KIND_LABELS[a] ?? a).localeCompare(DOC_KIND_LABELS[b] ?? b),
  )
  const opened = rows.find((p) => p.id === open)
  const openForm = (policy: Policy | null) => {
    setOpen(null)
    setEditing(policy)
    setShowForm(true)
  }

  return (
    <>
      <ErrorNote error={policies.error ?? submit.error} />

      {configurable && (
        <div className="bank-toolbar">
          <button className="primary" onClick={() => openForm(null)}>
            Draft a new rule
          </button>
          <span className="muted" style={{ fontSize: 13 }}>
            saved as a draft; it routes nothing until a Finance Head approves it
          </span>
        </div>
      )}

      {provisional.length > 0 && (
        <Note kind="warn">
          Some active rules are <strong>proposed defaults, not signed-off policy</strong>. They keep
          the system working until the business decides real limits — draft replacements here and
          have the Finance Head approve them.
        </Note>
      )}

      <div className="grid">
        <Stat
          label="Rules in force"
          value={String(active.length)}
          note={`covering ${covered.size} kind(s) of document`}
        />
        <Stat
          label="Drafts and pending"
          value={String(pending.length)}
          note={`${rows.filter((p) => p.status === 'pending_approval').length} with the Finance Head`}
        />
        <Stat
          label="Proposed defaults"
          value={String(provisional.length)}
          note={
            provisional.length ? 'in force, but not signed-off policy' : 'every rule is signed off'
          }
        />
        <Stat
          label="Replaced"
          value={String(rows.filter((p) => p.status === 'superseded').length)}
          note="kept: what was submitted under them keeps its route"
        />
      </div>

      <section className="card wide">
        <header className="appr-toolbar">
          <Tabs
            tabs={[
              { key: 'active', label: 'In force', count: active.length },
              {
                key: 'pending',
                label: 'Drafts & pending',
                count: pending.length,
                quietWhenZero: true,
              },
              {
                key: 'ended',
                label: 'Replaced',
                count: rows.filter((p) => p.status === 'superseded').length,
                quietWhenZero: true,
              },
              { key: 'all', label: 'All', count: rows.length },
            ]}
            value={tab}
            onChange={setTab}
          />
          <div className="appr-filters">
            <input
              type="search"
              placeholder="Document, step, person, department"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search rules"
            />
            <select
              value={docKind}
              onChange={(e) => setDocKind(e.target.value)}
              aria-label="Document"
            >
              <option value="">Every document</option>
              {kinds.map((k) => (
                <option key={k} value={k}>
                  {DOC_KIND_LABELS[k] ?? k}
                </option>
              ))}
            </select>
          </div>
        </header>
        <div className="body tight">
          {shown.length === 0 ? (
            <div className="empty">
              {rows.length === 0 ? 'No approval rules yet.' : 'No rule matches that.'}
            </div>
          ) : (
            <table className="appr-table">
              <thead>
                <tr>
                  <th>Document</th>
                  <th className="num">Amount band</th>
                  <th>Route</th>
                  <th>In force</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {paged.rows.map((p) => (
                  <tr key={p.id} {...rowProps(() => setOpen(p.id), open === p.id)}>
                    <td>
                      <div className="strong">{DOC_KIND_LABELS[p.doc_kind] ?? p.doc_kind}</div>
                      <div className="cell-sub">
                        {p.department_name ? `${p.department_name} only` : 'every department'}
                        {p.note && ' · proposed default'}
                      </div>
                    </td>
                    <td className="num">{band(p)}</td>
                    <td>
                      <Route policy={p} />
                      {p.min_quotations > 0 && (
                        <div className="cell-sub">needs {p.min_quotations} quotations</div>
                      )}
                    </td>
                    <td className="muted date">
                      {p.effective_from}
                      {p.effective_to ? ` – ${p.effective_to}` : ' onwards'}
                    </td>
                    <td>
                      <span className={`pill ${STATUS_CLASS[p.status] ?? ''}`}>
                        {STATUS_LABEL[p.status] ?? p.status.replace('_', ' ')}
                      </span>
                      {p.status !== 'active' && p.status !== 'superseded' && (
                        <div className="cell-sub">{p.approval_state}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <Pager page={paged.page} pages={paged.pages} onPage={paged.setPage} />
      </section>

      <details className="appr-how">
        <summary>How rules change</summary>
        <p className="muted">
          A new rule is a draft until a Finance Head approves it from the approval queue — the
          person who sets who approves what does not also approve that decision. A rule never starts
          in the past, and when it takes effect the rule it overlaps ends the day before. Requests
          already submitted keep the route they were sent on.
        </p>
      </details>

      {opened && (
        <SidePanel
          kicker="Approval rule"
          title={DOC_KIND_LABELS[opened.doc_kind] ?? opened.doc_kind}
          sub={`${band(opened)} · ${opened.department_name ? `${opened.department_name} only` : 'every department'}`}
          badge={
            <span className={`pill ${STATUS_CLASS[opened.status] ?? ''}`}>
              {STATUS_LABEL[opened.status] ?? opened.status}
            </span>
          }
          onClose={() => setOpen(null)}
          foot={
            configurable && (opened.status === 'draft' || opened.status === 'rejected') ? (
              <>
                <ErrorNote error={submit.error} />
                <button
                  className="primary"
                  disabled={submit.isPending}
                  onClick={() => submit.mutate(opened.id)}
                >
                  Send for approval
                </button>
                <button onClick={() => openForm(opened)}>Edit the draft</button>
              </>
            ) : opened.status === 'pending_approval' ? (
              <span className="faint">
                With the Finance Head, in the approval queue — {opened.approval_state}.
              </span>
            ) : undefined
          }
        >
          {opened.note && (
            <Note kind="warn">
              <strong>Proposed default:</strong> {opened.note}
            </Note>
          )}
          <section>
            <h3>The route, in order</h3>
            <ol className="appr-route">
              {opened.steps.map((s) => (
                <li key={s.step_no}>
                  <div className="route-head">
                    <strong>{s.label}</strong>
                    {s.required_count > 1 && (
                      <span className="pill advisory">{s.required_count} people</span>
                    )}
                  </div>
                  <div className="muted">
                    {s.kind === 'department_authority'
                      ? 'the department’s approval authority on the day'
                      : s.kind === 'named_user'
                        ? `${s.named_user_name ?? 'a named person'}, by name`
                        : `anyone holding ${s.permission ?? 'the approve permission'}`}
                  </div>
                </li>
              ))}
            </ol>
          </section>
          <section>
            <h3>Conditions</h3>
            <dl className="appr-facts">
              <div>
                <dt>In force</dt>
                <dd>
                  {opened.effective_from}
                  {opened.effective_to ? ` – ${opened.effective_to}` : ' onwards'}
                </dd>
              </div>
              <div>
                <dt>Days to respond</dt>
                <dd>{opened.sla_days}</dd>
              </div>
              <div>
                <dt>Supporting document</dt>
                <dd>{opened.requires_document ? 'required' : 'not required'}</dd>
              </div>
              {opened.doc_kind === 'purchase_request' && (
                <div>
                  <dt>Quotations</dt>
                  <dd>{opened.min_quotations || 'none'} needed</dd>
                </div>
              )}
              <div>
                <dt>One person, both steps</dt>
                <dd>
                  {opened.combined_step_ceiling_paise === null
                    ? 'never'
                    : `up to ${formatInr(opened.combined_step_ceiling_paise)}`}
                </dd>
              </div>
              <div>
                <dt>Drafted by</dt>
                <dd>{opened.created_by_name ?? '—'}</dd>
              </div>
            </dl>
          </section>
        </SidePanel>
      )}

      {configurable && showForm && (
        <SidePanel
          kicker="Approval rule"
          title={editing ? 'Edit draft rule' : 'Draft a rule'}
          sub="saved as a draft; nothing changes yet"
          wide
          onClose={() => {
            setShowForm(false)
            setEditing(null)
          }}
        >
          <PolicyForm
            key={editing?.id ?? 'new'}
            policy={editing}
            options={options.data}
            departments={departments.data ?? []}
            onDone={() => {
              setShowForm(false)
              setEditing(null)
              setTab('pending')
              void queryClient.invalidateQueries({ queryKey: ['approvals'] })
            }}
          />
        </SidePanel>
      )}
    </>
  )
}

function PolicyForm({
  policy,
  options,
  departments,
  onDone,
}: {
  policy: Policy | null
  options: RouteOptions | undefined
  departments: Department[]
  onDone: () => void
}) {
  const [form, setForm] = useState({
    doc_kind: policy?.doc_kind ?? 'purchase_request',
    min: rupees(policy?.min_amount_paise ?? 0),
    max: rupees(policy?.max_amount_paise ?? null),
    effective_from: policy && policy.effective_from >= today() ? policy.effective_from : today(),
    department_id: policy?.department_id ?? '',
    sla_days: policy?.sla_days ?? 3,
    requires_document: policy?.requires_document ?? false,
    ceiling: rupees(policy?.combined_step_ceiling_paise ?? null),
    min_quotations: policy?.min_quotations ?? 0,
    note: policy?.note ?? '',
  })
  const [steps, setSteps] = useState<StepDraft[]>(
    policy
      ? policy.steps.map((s) => ({
          kind: s.kind,
          label: s.label,
          permission: s.permission ?? '',
          named_user_id: s.named_user_id ?? '',
          required_count: s.required_count,
        }))
      : DEFAULT_STEPS,
  )

  const permissionFor = (kind: string) =>
    options?.permissions.find((p) => p.startsWith(kindPrefix(kind))) ?? ''

  const save = useMutation({
    mutationFn: () => {
      const body = {
        doc_kind: form.doc_kind,
        min_amount_paise: toPaise(form.min) ?? 0,
        max_amount_paise: toPaise(form.max),
        effective_from: form.effective_from,
        department_id: form.department_id || null,
        requires_document: form.requires_document,
        sla_days: form.sla_days,
        combined_step_ceiling_paise: toPaise(form.ceiling),
        min_quotations: form.doc_kind === 'purchase_request' ? form.min_quotations : 0,
        note: form.note || null,
        steps: steps.map((s) => ({
          kind: s.kind,
          label: s.label,
          permission: s.kind === 'permission' ? s.permission || permissionFor(form.doc_kind) : null,
          named_user_id: s.kind === 'named_user' ? s.named_user_id || null : null,
          required_count: s.kind === 'permission' ? s.required_count : 1,
        })),
      }
      return policy
        ? api.put<Policy>(`/approvals/policies/${policy.id}`, body)
        : api.post<Policy>('/approvals/policies', body)
    },
    onSuccess: onDone,
  })

  const update = (i: number, change: Partial<StepDraft>) =>
    setSteps(steps.map((s, j) => (j === i ? { ...s, ...change } : s)))

  return (
    <>
      <ErrorNote error={save.error} />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          save.mutate()
        }}
      >
        <div className="row">
          <Field label="Document">
            <select
              value={form.doc_kind}
              onChange={(e) => setForm({ ...form, doc_kind: e.target.value })}
            >
              {(options?.doc_kinds ?? []).map((k) => (
                <option key={k} value={k}>
                  {DOC_KIND_LABELS[k] ?? k}
                </option>
              ))}
            </select>
          </Field>
          <Field label="From ₹" caption="inclusive">
            <input
              className="num"
              value={form.min}
              onChange={(e) => setForm({ ...form, min: e.target.value })}
              required
            />
          </Field>
          <Field label="Below ₹" caption="empty means and above">
            <input
              className="num"
              value={form.max}
              onChange={(e) => setForm({ ...form, max: e.target.value })}
            />
          </Field>
          <Field label="Starts">
            <input
              type="date"
              min={today()}
              value={form.effective_from}
              onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
              required
            />
          </Field>
        </div>
        <div className="row">
          <Field label="Only for department" caption="empty means every department">
            <select
              value={form.department_id}
              onChange={(e) => setForm({ ...form, department_id: e.target.value })}
            >
              <option value="">All departments</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Days to respond">
            <input
              type="number"
              min={1}
              max={60}
              value={form.sla_days}
              onChange={(e) => setForm({ ...form, sla_days: Number(e.target.value) })}
            />
          </Field>
          <Field
            label="One person may cover department + finance up to ₹"
            caption="empty means never"
          >
            <input
              className="num"
              value={form.ceiling}
              onChange={(e) => setForm({ ...form, ceiling: e.target.value })}
            />
          </Field>
        </div>

        <h3 style={{ margin: '12px 0 4px' }}>Route, in order</h3>
        {steps.map((s, i) => (
          <div className="row" key={i}>
            <Field label={`Step ${i + 1}`}>
              <select
                value={s.kind}
                onChange={(e) => update(i, { kind: e.target.value as StepKind })}
              >
                <option value="department_authority">The department's approval authority</option>
                <option value="permission">Anyone holding a permission</option>
                <option value="named_user">A named person</option>
              </select>
            </Field>
            <Field label="Name shown">
              <input
                value={s.label}
                onChange={(e) => update(i, { label: e.target.value })}
                required
              />
            </Field>
            {s.kind === 'permission' && (
              <>
                <Field label="Permission">
                  <select
                    value={s.permission || permissionFor(form.doc_kind)}
                    onChange={(e) => update(i, { permission: e.target.value })}
                  >
                    {(options?.permissions ?? []).map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="People needed">
                  <input
                    type="number"
                    min={1}
                    max={3}
                    value={s.required_count}
                    onChange={(e) => update(i, { required_count: Number(e.target.value) })}
                  />
                </Field>
              </>
            )}
            {s.kind === 'named_user' && (
              <Field label="Person">
                <select
                  value={s.named_user_id}
                  onChange={(e) => update(i, { named_user_id: e.target.value })}
                  required
                >
                  <option value="">Choose…</option>
                  {(options?.people ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <div className="shrink">
              <button
                type="button"
                className="small"
                disabled={steps.length === 1}
                onClick={() => setSteps(steps.filter((_, j) => j !== i))}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
        {steps.length < 5 && (
          <button
            type="button"
            className="small"
            onClick={() =>
              setSteps([
                ...steps,
                {
                  kind: 'permission',
                  label: 'Second finance approval',
                  permission: '',
                  named_user_id: '',
                  required_count: 1,
                },
              ])
            }
          >
            Add a step
          </button>
        )}

        <div className="row" style={{ marginTop: 12 }}>
          <Field label="Supporting document required">
            <select
              value={form.requires_document ? 'yes' : 'no'}
              onChange={(e) => setForm({ ...form, requires_document: e.target.value === 'yes' })}
            >
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </Field>
          {form.doc_kind === 'purchase_request' && (
            <Field
              label="Quotations needed"
              caption="from different vendors, each with its document"
            >
              <input
                type="number"
                min={0}
                max={5}
                value={form.min_quotations}
                onChange={(e) => setForm({ ...form, min_quotations: Number(e.target.value) })}
              />
            </Field>
          )}
          <Field label="Note">
            <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </Field>
        </div>
        <div className="row">
          <div className="shrink">
            <button className="primary" type="submit" disabled={save.isPending}>
              Save draft
            </button>
          </div>
          <div className="shrink">
            <button type="button" onClick={onDone}>
              Cancel
            </button>
          </div>
        </div>
      </form>
    </>
  )
}

/** The approve permission that matches a document kind, as a default. */
function kindPrefix(kind: string): string {
  const map: Record<string, string> = {
    manual_journal: 'gl.journal.approve',
    purchase_request: 'proc.purchase_request.approve',
    purchase_order: 'proc.purchase_order.approve',
    payment: 'ap.payment.approve',
    vendor_invoice: 'ap.invoice.approve',
    customer_invoice: 'ar.invoice.approve',
    vendor: 'party.vendor.approve',
    vendor_bank_change: 'party.vendor_bank.approve',
    expense_claim: 'exp.claim.approve',
    payment_request: 'spend.payment_request.approve',
    quotation_exception: 'proc.purchase_request.approve',
    approval_rule: 'org.approval_rule.approve',
  }
  return map[kind] ?? ''
}
